// v3 customer-workbook specs: each sized use case's arithmetic, defined once as data.
//
// A spec is the ordered calculation rows of one use case, one section per record level (CRM
// enrichment and TAM sourcing have an Accounts and a Contacts chain; every other use case has one
// section). Each row is an `Expr` (engine/xlsx-expr.ts) that renders to the live Excel formula AND
// evaluates in JS with the engine's semantics. __tests__/xlsx-use-case-spec.test.ts pins the JS
// side to golden-v3.json and to computeUseCase, and fails if a catalog question is added, removed
// or renamed without this file following. When use-case-engine.ts FORMULAS change (and the golden
// fixtures are regenerated), update the matching spec here.
//
// Conventions shared with the v2 workbook (xlsx-expr.ts):
//  - Question inputs are referenced by their catalog key. Percent questions go through `pct(key)`
//    (the workbook stores 25% as 0.25; the engine stores 25), everything else through `ref(key)`.
//  - A calc row that is itself a rate (a change in fill rate, a lifted conversion) is a FRACTION in
//    both renderers. Count / money rows are identical in both, so those are what the parity test
//    compares.
//  - "With Clay − today" differences are floored at 0 (`max0`), as in the engine's `diff`.
//  - `sub` on a row is the engine's sub-total key for it (records, opportunities, pipeline,
//    revenue, ...), so the parity test can line the two up by name.

import { getSizedUseCase, type RecordLevel } from '../data/use-case-catalog';
import type { NumFmt } from '../xlsx/writer';
import { add, collectRefs, div, evalExpr, lit, max0, mul, pct, ref, sub, sum } from './xlsx-expr';
import type { Expr } from './xlsx-expr';

export interface SpecRow {
  key: string;
  label: string;
  expr: Expr;
  fmt: NumFmt;
  /** The engine sub-total this row corresponds to, when it is one. */
  sub?: string;
}

export interface SpecSection {
  level: RecordLevel | null;
  title: string;
  rows: SpecRow[];
  /** The row that is this section's gross value (revenue, or cost saved). */
  valueKey: string;
}

export interface UseCaseSpec {
  id: string;
  sections: SpecSection[];
  /** Tool consolidation only: the keys of its displaced-tool input rows, in sheet order. */
  toolKeys: string[];
  /**
   * Use cases with calculation methods (GTM automation) only: one 1 / 0 switch input per method,
   * `{catalog method id: switch key}`. The formulas multiply each method's hours by its switch, so the
   * workbook stays live (the customer can flip a method) and one spec covers every combination.
   */
  methodSwitches: Record<string, string>;
}

/* ------------------------------------------------------------------- shared keys */

/** Deal-level keys a spec may read. ACV and current pipeline are plain refs; win rate is a pct. */
export const DEAL_KEYS = ['deal_acv', 'deal_win_rate', 'deal_current_pipeline'] as const;

/** Workbook constants; each gets a Constants-sheet cell the formulas reference. */
export const USE_CASE_CONSTANTS: Record<string, { v: number; label: string; note: string }> = {
  c_months: { v: 12, label: 'Months / year', note: 'Annualizes monthly volumes' },
  c_weeks_work: { v: 48, label: 'Working weeks / year', note: 'Rep prospecting annualizes weekly prospects over 48 working weeks' },
  c_weeks_year: { v: 52, label: 'Weeks / year', note: 'GTM automation annualizes weekly hours' },
  c_minutes_hour: { v: 60, label: 'Minutes / hour', note: 'GTM automation converts minutes saved per record into hours' },
  c_hours_year: { v: 2080, label: 'Work hours / year', note: 'Fully-loaded cost per hour = annual cost ÷ 2,080' },
};

const row = (key: string, label: string, expr: Expr, fmt: NumFmt, subKey?: string): SpecRow => ({
  key,
  label,
  expr,
  fmt,
  ...(subKey ? { sub: subKey } : {}),
});

/** With Clay − today, floored at zero, as a fraction (both operands are percent questions). */
const pctDiff = (clay: string, today: string): Expr => max0(sub(pct(clay), pct(today)));

/** Win-rate increase as a fraction: today's win rate × the relative lift (both percent questions). */
const winRateIncrease = (liftKey: string): Expr => mul(pct('deal_win_rate'), pct(liftKey));

/** × ACV = pipeline × win rate = revenue. Keys are prefixed so two levels never collide. */
function pipelineTail(p: string, oppsKey: string, acvKey = 'deal_acv', acvLabel = 'Average deal size'): SpecRow[] {
  return [
    row(`${p}pipeline`, `Pipeline (opportunities × ${acvLabel.toLowerCase()})`, mul(ref(oppsKey), ref(acvKey)), 'money', 'pipeline'),
    row(`${p}revenue`, 'Revenue (pipeline × win rate)', mul(ref(`${p}pipeline`), pct('deal_win_rate')), 'money', 'revenue'),
  ];
}

const sectionOf = (level: RecordLevel | null, title: string, rows: SpecRow[], valueKey: string): SpecSection => ({
  level,
  title,
  rows,
  valueKey,
});

/* ------------------------------------------------------------------------- specs */

const SPECS: Record<string, UseCaseSpec> = {};
const def = (id: string, sections: SpecSection[], methodSwitches: Record<string, string> = {}) => {
  SPECS[id] = { id, sections, toolKeys: [], methodSwitches };
};

// Q26 / Q32: only the baseline fill / contactable change counts; contacts add a share-worked haircut.
def('uc-crm-enrichment', [
  sectionOf(
    'accounts',
    'Accounts',
    [
      row('a_actionable', 'Account records made actionable', mul(ref('accounts_records'), pctDiff('accounts_fill_clay', 'accounts_fill_current')), 'count', 'records'),
      row('a_opps', 'Opportunities', mul(ref('a_actionable'), pct('accounts_record_to_opp')), 'num1', 'opportunities'),
      ...pipelineTail('a_', 'a_opps'),
    ],
    'a_revenue',
  ),
  sectionOf(
    'contacts',
    'Contacts',
    [
      row('c_reachable', 'Contacts made reachable', mul(ref('contacts_records'), pctDiff('contacts_reachable_clay', 'contacts_reachable_current')), 'count', 'records'),
      row('c_worked', 'Contacts worked', mul(ref('c_reachable'), pct('contacts_share_worked')), 'count', 'worked'),
      row('c_meetings', 'Meetings', mul(ref('c_worked'), pct('contacts_to_meeting')), 'num1', 'meetings'),
      row('c_opps', 'Opportunities', mul(ref('c_meetings'), pct('contacts_meeting_to_opp')), 'num1', 'opportunities'),
      ...pipelineTail('c_', 'c_opps'),
    ],
    'c_revenue',
  ),
]);

// Q27: win-rate path. Reads the deal's current annual pipeline; blank evaluates to 0.
def('uc-account-lead-scoring', [
  sectionOf(
    null,
    'Scoring',
    [
      row('scored', 'Pipeline newly scored', mul(ref('deal_current_pipeline'), pctDiff('coverage_clay', 'coverage_current')), 'money', 'scored_pipeline'),
      row('win_inc', 'Win-rate increase (win rate × relative lift)', winRateIncrease('winrate_lift'), 'pct'),
      row('revenue', 'Revenue (× win-rate increase)', mul(ref('scored'), ref('win_inc')), 'money', 'revenue'),
    ],
    'revenue',
  ),
]);

// Q28 / Q33: only incremental sourcing counts.
def('uc-tam-sourcing', [
  sectionOf(
    'accounts',
    'Accounts',
    [
      row('a_net', 'Net-new accounts (with Clay − today)', max0(sub(ref('accounts_year_clay'), ref('accounts_year_today'))), 'count', 'records'),
      row('a_opps', 'Opportunities', mul(ref('a_net'), pct('accounts_to_opp')), 'num1', 'opportunities'),
      ...pipelineTail('a_', 'a_opps'),
    ],
    'a_revenue',
  ),
  sectionOf(
    'contacts',
    'Contacts',
    [
      row('c_net', 'Net-new contacts (with Clay − today)', max0(sub(ref('contacts_year_clay'), ref('contacts_year_today'))), 'count', 'records'),
      row('c_meetings', 'Meetings', mul(ref('c_net'), pct('contacts_to_meeting')), 'num1', 'meetings'),
      row('c_opps', 'Opportunities', mul(ref('c_meetings'), pct('contacts_meeting_to_opp')), 'num1', 'opportunities'),
      ...pipelineTail('c_', 'c_opps'),
    ],
    'c_revenue',
  ),
]);

// Q44: today vs with Clay on the same base. Unmatched leads convert at 0 (the match rate is a
// factor of both chains), and the additional opportunities are floored at 0.
def('uc-inbound-speed-to-lead', [
  sectionOf(
    null,
    'Inbound',
    [
      row('opps_today', 'Opportunities today (leads × qualified × match × conversion)', mul(ref('leads_year'), pct('qualified_pct'), pct('match_current'), pct('conversion_current')), 'num1'),
      row('conv_clay', 'Lead-to-opportunity conversion with Clay (today × (1 + lift))', mul(pct('conversion_current'), add(lit(1), pct('speed_lift'))), 'pct'),
      row('opps_clay', 'Opportunities with Clay (leads × qualified × match × lifted conversion)', mul(ref('leads_year'), pct('qualified_pct'), pct('match_clay'), ref('conv_clay')), 'num1'),
      row('opps', 'Additional opportunities (with Clay − today)', max0(sub(ref('opps_clay'), ref('opps_today'))), 'num1', 'opportunities'),
      ...pipelineTail('', 'opps'),
    ],
    'revenue',
  ),
]);

// Q40: starts from the outreach volume sent through automated outbound.
def('uc-automated-outbound', [
  sectionOf(
    null,
    'Outbound',
    [
      row('meetings', 'Meetings (outreach sent × meeting rate)', mul(ref('outreach_year'), pct('meeting_rate')), 'num1', 'meetings'),
      row('opps', 'Opportunities', mul(ref('meetings'), pct('meeting_to_opp')), 'num1', 'opportunities'),
      ...pipelineTail('', 'opps'),
    ],
    'revenue',
  ),
]);

// Q41: cost path — wasted ad spend recovered.
def('uc-performance-marketing', [
  sectionOf(
    null,
    'Ad spend',
    [
      row('wasted', 'Spend no longer wasted (ad spend × change in match rate)', mul(ref('ad_spend_year'), pctDiff('match_clay', 'match_current')), 'money', 'spend_reached'),
      row('saved', 'Wasted spend recovered (× recoverable share)', mul(ref('wasted'), pct('recoverable_pct')), 'money', 'cost_saved'),
    ],
    'saved',
  ),
]);

// Q42: no rep-hours value here — labor dollars live only in GTM automation.
def('uc-rep-prospecting', [
  sectionOf(
    null,
    'Prospecting',
    [
      row('per_rep', 'Added prospects per rep per week (with Clay − today)', max0(sub(ref('prospects_week_clay'), ref('prospects_week_today'))), 'count', 'added_per_rep'),
      row('prospects', 'Added prospects per year (× reps × 48 weeks)', mul(ref('per_rep'), ref('reps'), ref('c_weeks_work')), 'count', 'prospects'),
      row('meetings', 'Meetings', mul(ref('prospects'), pct('meeting_rate')), 'num1', 'meetings'),
      row('opps', 'Opportunities', mul(ref('meetings'), pct('meeting_to_opp')), 'num1', 'opportunities'),
      ...pipelineTail('', 'opps'),
    ],
    'revenue',
  ),
]);

// Q46: the value is the rate gain from timing.
def('uc-signal-based-outbound', [
  sectionOf(
    null,
    'Signals',
    [
      row('contacts', 'Signal-triggered contacts per year (× 12)', mul(ref('signal_contacts_month'), ref('c_months')), 'count', 'contacts'),
      row('rate_change', 'Change in meeting rate (with Clay − today)', pctDiff('meeting_rate_clay', 'meeting_rate_today'), 'pct'),
      row('meetings', 'Added meetings (× change in meeting rate)', mul(ref('contacts'), ref('rate_change')), 'num1', 'meetings'),
      row('opps', 'Opportunities', mul(ref('meetings'), pct('meeting_to_opp')), 'num1', 'opportunities'),
      ...pipelineTail('', 'opps'),
    ],
    'revenue',
  ),
]);

// Q47: valued at the PLG ACV (its own input, which defaults to the company ACV).
def('uc-plg-expansion', [
  sectionOf(
    null,
    'Signups',
    [
      row('signups', 'Signups per year (× 12)', mul(ref('signups_month'), ref('c_months')), 'count', 'signups'),
      row('icp', 'ICP-fit signups', mul(ref('signups'), pct('icp_fit')), 'count', 'icp_signups'),
      row('conv_change', 'Change in conversion (with Clay − today)', pctDiff('conversion_clay', 'conversion_today'), 'pct'),
      row('opps', 'Added opportunities (× change in conversion)', mul(ref('icp'), ref('conv_change')), 'num1', 'opportunities'),
      ...pipelineTail('', 'opps', 'plg_acv', 'PLG average deal size'),
    ],
    'revenue',
  ),
]);

// Q49: win-rate path — accounts enrolled is program size, not a delta.
def('uc-abm', [
  sectionOf(
    null,
    'ABM',
    [
      row('opps', 'Opportunities (accounts enrolled × share with an open opportunity)', mul(ref('accounts_enrolled'), pct('open_opp_pct')), 'num1', 'opportunities'),
      row('pipeline', 'Pipeline on ABM accounts (× average deal size)', mul(ref('opps'), ref('deal_acv')), 'money', 'abm_pipeline'),
      row('win_inc', 'Win-rate increase (win rate × relative uplift)', winRateIncrease('winrate_uplift'), 'pct'),
      row('revenue', 'Revenue (× win-rate increase)', mul(ref('pipeline'), ref('win_inc')), 'money', 'revenue'),
    ],
    'revenue',
  ),
]);

// Q51: the single use case carrying labor dollars. Three time-savings methods; each method's hours
// per year are multiplied by its 1 / 0 switch, summed, and the hourly rate applies once. The
// engine's per-method sub-totals (`hours_year_<id>`) exist only with two or more methods on; with
// one on, its hours per year IS the total (`hours_year`), which the total row here reproduces.
def(
  'uc-gtm-automation',
  [
    sectionOf(
      null,
      'Automation',
      [
        row('weekly', 'Method A: automated hours per week (team hours × automation share)', mul(ref('hours_week'), pct('automation_pct')), 'hrs', 'hours_week'),
        row('yearly_share', 'Method A: automated hours per year (× 52)', mul(ref('weekly'), ref('c_weeks_year')), 'hrs', 'hours_year_share'),
        row('yearly_flat', 'Method B: flat hours saved per year (× 52)', mul(ref('hours_saved_week'), ref('c_weeks_year')), 'hrs', 'hours_year_flat'),
        row('yearly_records', 'Method C: record-based hours saved per year (minutes × records ÷ 60 × 52)', div(mul(ref('minutes_per_record'), ref('records_week'), ref('c_weeks_year')), ref('c_minutes_hour')), 'hrs', 'hours_year_records'),
        row(
          'yearly',
          'Total hours saved per year (selected methods)',
          add(mul(ref('yearly_share'), ref('m_share')), mul(ref('yearly_flat'), ref('m_flat')), mul(ref('yearly_records'), ref('m_records'))),
          'hrs',
          'hours_year',
        ),
        row('rate', 'Cost per hour (annual cost ÷ 2,080)', div(ref('annual_cost'), ref('c_hours_year')), 'money2'),
        row('saved', 'Labor cost saved', mul(ref('yearly'), ref('rate')), 'money', 'cost_saved'),
      ],
      'saved',
    ),
  ],
  { share: 'm_share', flat: 'm_flat', records: 'm_records' },
);

export const TOOL_CONSOLIDATION_ID = 'uc-tool-consolidation';

/** Q50: the checked tools' contract values are the tool spend removed — Σ of the tool rows. */
export function toolConsolidationSpec(toolCount: number): UseCaseSpec {
  const toolKeys = Array.from({ length: toolCount }, (_, i) => `tool_${i + 1}`);
  return {
    id: TOOL_CONSOLIDATION_ID,
    toolKeys,
    methodSwitches: {},
    sections: [
      sectionOf(
        null,
        'Tools',
        [row('saved', 'Tool spend removed (Σ displaced contracts)', sum(toolKeys), 'money', 'cost_saved')],
        'saved',
      ),
    ],
  };
}

/** Every sized use case except tool consolidation, whose spec depends on the checklist length. */
export const USE_CASE_SPECS: Readonly<Record<string, UseCaseSpec>> = SPECS;

/** The spec for a sized use case. `toolCount` only matters for tool consolidation. */
export function getUseCaseSpec(id: string, toolCount = 0): UseCaseSpec {
  if (id === TOOL_CONSOLIDATION_ID) return toolConsolidationSpec(toolCount);
  const spec = SPECS[id];
  if (!spec) throw new Error(`xlsx-use-case-specs: no spec for ${id}`);
  return spec;
}

/** The sections a selection has on: the enabled record levels, or the single level-less section. */
export function sectionsFor(spec: UseCaseSpec, levels: readonly RecordLevel[]): SpecSection[] {
  return spec.sections.filter((s) => s.level === null || levels.includes(s.level));
}

/**
 * The question keys (and tool keys) a section reads: every reference that is not one of its own
 * calc rows, a deal key or a constant. This is what the drift test compares to the catalog.
 */
export function sectionInputKeys(section: SpecSection): string[] {
  const own = new Set(section.rows.map((r) => r.key));
  const found = new Set<string>();
  for (const r of section.rows) {
    const { refs, pcts } = collectRefs(r.expr);
    for (const k of [...refs, ...pcts]) found.add(k);
  }
  return [...found].filter((k) => !own.has(k) && !(DEAL_KEYS as readonly string[]).includes(k) && !(k in USE_CASE_CONSTANTS));
}

export function specInputKeys(spec: UseCaseSpec): string[] {
  return [...new Set(spec.sections.flatMap(sectionInputKeys))];
}

/** Does the spec read this deal key anywhere? */
export function specReadsDealKey(spec: UseCaseSpec, key: (typeof DEAL_KEYS)[number]): boolean {
  return spec.sections.some((s) =>
    s.rows.some((r) => {
      const { refs, pcts } = collectRefs(r.expr);
      return refs.has(key) || pcts.has(key);
    }),
  );
}

/**
 * Evaluate a section with the engine's semantics. `values` holds the resolved answers (whole
 * percents), the deal keys (ACV, win rate in whole percent, current pipeline) and tool spends;
 * constants are filled in here. Returns every row's value by key.
 */
export function evalSection(section: SpecSection, values: Record<string, number>): Record<string, number> {
  const vals: Record<string, number> = { ...values };
  for (const [k, c] of Object.entries(USE_CASE_CONSTANTS)) vals[k] = c.v;
  const env = {
    timeAsTime: false,
    get: (k: string) => {
      if (!(k in vals)) throw new Error(`xlsx-use-case-specs: unresolved reference ${k}`);
      return vals[k];
    },
  };
  const out: Record<string, number> = {};
  for (const r of section.rows) {
    vals[r.key] = evalExpr(r.expr, env);
    out[r.key] = vals[r.key];
  }
  return out;
}

/** Catalog check used by the workbook builder: is this id something we can write? */
export function hasUseCaseSpec(id: string): boolean {
  return !!getSizedUseCase(id) && (id === TOOL_CONSOLIDATION_ID || id in SPECS);
}
